import Dexie, { Table } from 'dexie'
import { Signal } from '@/hooks/use-signals'

export interface CachedSignal extends Signal {
  cachedAt: number
  isOffline: boolean
}

export interface UserPreferences {
  id: string
  userId: string
  notificationsEnabled: boolean
  telegramEnabled: boolean
  discordEnabled: boolean
  telegramToken?: string
  telegramChatId?: string  // Nuevo campo para Chat ID de Telegram
  discordWebhook?: string
  subscribedTopics: string[]
  language: 'en' | 'es'
  theme: 'dark' | 'light'
  updatedAt: number
}

export class RiskoPulseDB extends Dexie {
  signals!: Table<CachedSignal>
  userPreferences!: Table<UserPreferences>

  constructor() {
    super('RiskoPulseDB')
    this.version(1).stores({
      signals: 'id, symbol, side, status, createdAt, cachedAt, isOffline',
      userPreferences: 'id, userId, notificationsEnabled, telegramEnabled, discordEnabled, subscribedTopics, language, theme, updatedAt'
    })
  }
}

export const db = new RiskoPulseDB()

// Utility functions for offline caching
export class OfflineCache {
  static async cacheSignals(signals: Signal[]): Promise<void> {
    try {
      const cachedSignals: CachedSignal[] = signals.map(signal => ({
        ...signal,
        cachedAt: Date.now(),
        isOffline: false
      }))

      await db.signals.bulkPut(cachedSignals)
      console.log(`Cached ${signals.length} signals`)
    } catch (error) {
      console.error('Error caching signals:', error)
    }
  }

  static async getCachedSignals(limit: number = 100): Promise<CachedSignal[]> {
    try {
      const signals = await db.signals
        .orderBy('createdAt')
        .reverse()
        .limit(limit)
        .toArray()
      
      return signals
    } catch (error) {
      console.error('Error getting cached signals:', error)
      return []
    }
  }

  static async clearOldSignals(olderThanDays: number = 7): Promise<void> {
    try {
      const cutoffTime = Date.now() - (olderThanDays * 24 * 60 * 60 * 1000)
      await db.signals.where('cachedAt').below(cutoffTime).delete()
      console.log('Cleared old cached signals')
    } catch (error) {
      console.error('Error clearing old signals:', error)
    }
  }

  static async clearSignals(): Promise<void> {
    try {
      await db.signals.clear()
      console.log('Cleared cached signals')
    } catch (error) {
      console.error('Error clearing cached signals:', error)
    }
  }

  static async getUserPreferences(userId: string): Promise<UserPreferences | null> {
    try {
      const prefs = await db.userPreferences.where('userId').equals(userId).first()
      return prefs || null
    } catch (error) {
      console.error('Error getting user preferences:', error)
      return null
    }
  }

  static async saveUserPreferences(preferences: UserPreferences): Promise<void> {
    try {
      await db.userPreferences.put({
        ...preferences,
        updatedAt: Date.now()
      })
      console.log('User preferences saved')
    } catch (error) {
      console.error('Error saving user preferences:', error)
    }
  }

  static async updateSignalStatus(signalId: string, status: Signal['status']): Promise<void> {
    try {
      await db.signals.update(signalId, { 
        status,
        cachedAt: Date.now()
      })
    } catch (error) {
      console.error('Error updating signal status:', error)
    }
  }

  static async getSignalCount(): Promise<number> {
    try {
      return await db.signals.count()
    } catch (error) {
      console.error('Error getting signal count:', error)
      return 0
    }
  }

  static async isOffline(): Promise<boolean> {
    return !navigator.onLine
  }

  static async markSignalsAsOffline(): Promise<void> {
    try {
      await db.signals.toCollection().modify({ isOffline: true })
    } catch (error) {
      console.error('Error marking signals as offline:', error)
    }
  }

  static async markSignalsAsOnline(): Promise<void> {
    try {
      await db.signals.toCollection().modify({ isOffline: false })
    } catch (error) {
      console.error('Error marking signals as online:', error)
    }
  }
}

// Initialize database and clean up old data
export async function initializeOfflineCache(): Promise<void> {
  try {
    await db.open()
    await OfflineCache.clearOldSignals(7) // Keep only last 7 days
    console.log('Offline cache initialized')
  } catch (error) {
    console.error('Error initializing offline cache:', error)
  }
}
